import { useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useExtractSeasonsFromScope } from "~pages/Dashboard/components/DashboardSeasonList/hooks/useExtractSeasonsFromScope.ts";
import { useGetDashboardSeasonsDataTableColumns } from "~pages/Dashboard/components/DashboardSeasonList/hooks/useGetDashboardSeasonsDataTableColumns.tsx";
import { useGetDashboardSeasonsDataTableFilters } from "~pages/Dashboard/components/DashboardSeasonList/hooks/useGetDashboardSeasonsDataTableFilters.tsx";
import { useFilteredSeasons } from "~pages/Seasons/SeasonListPage/hooks/useFilteredSeasons.ts";

export const DashboardSeasonList = () => {
  const { sport, customerId, organizationId } = useParams();
  const { isLoading } = useGetCurrentUserPermissionScope();
  const navigate = useNavigate();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.dashboard.listPage;
  const paginationConfig = useHandlePagination(50, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const [searchText, setSearchText] = useState("");
  const navigateToSeason = (seasonId: string) => navigate(`/${customerId}/admin/${sport}/${organizationId}/seasons/${seasonId}/fixtures`);

  const filtersConfig = useGetDashboardSeasonsDataTableFilters();
  const columns = useGetDashboardSeasonsDataTableColumns();

  const allSeasons = useExtractSeasonsFromScope();

  const clientSeasons = useFilteredSeasons({
    seasons: allSeasons,
    currentFilter: filtersConfig.currentFilter,
    searchText,
  });

  const { formatMessage } = useIntl();

  /*
    It uses organizationId as part of the key because some filters are organization-specific.
    This prevents incorrect filter values when switching between organizations when loading filters from local storage.
   */
  const filtersLocalStorageKey = `${sport}.${organizationId}.tables.settings.filters.dashboard.seasons`;

  return (
    <DataTable
      title={formatMessage({ id: "seasonsListPage.title" })}
      columns={columns}
      rowKey={"seasonId"}
      dataSource={clientSeasons}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.dashboard.seasons`}
      isDataLoading={isLoading}
      filtersConfig={{
        ...filtersConfig,
        filtersLocalStorageKey,
      }}
      onRowClick={(season) => {
        return {
          onClick: () => navigateToSeason(season.seasonId),
        };
      }}
      searchConfig={{
        handleSearch: setSearchText,
        searchText: searchText,
      }}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.dashboard.seasons`,
        defaultSorter: {
          columnKey: "year",
          order: "descend",
        },
      }}
      permissionNames={[PermissionNames.SEASON]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, total: clientSeasons.length }}
    />
  );
};
